import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorModal from '../../../../Components/ErrorModal';
import SuccesModal from '../../../../Components/SuccesModal';
import DeleteAccountModal from '../../../../Components/DeleteAccountModal';
import { deleteUser, getEmailSettings, updateEmailSettings, updatePassword, requestGdprExport } from '../../../../Requests/User/UserRequests';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import PrivacyDashboard from '../../../../Components/Privacy/PrivacyDashboard';
import MemberAvatar from '../../../../Components/Team/MemberAvatar';
import { getTeamMembers } from '../../../../Requests/TeamRequests';
import { getTeamUuid, getUserUuid } from '../../../../utils/cookies';
import { ButtonParticleLoader } from "../../../../Components/ParticleLoader";

function AccountSettings() {
    const { t } = useTranslation();
    const [newPasswordIsLoading, setNewPasswordIsLoading] = useState(false)
    const [updateEmailSettingsLoading, setUpdateEmailSettingsLoading] = useState(false)
    const [gdprExportLoading, setGdprExportLoading] = useState(false)
    const [feedbackModal, setFeedbackModal] = useState(null)
    const [emailSettings, setEmailSettings] = useState(null)
    const [me, setMe] = useState(null)

    let handleCancelUser = async (event) => {
        let reset = await setFeedbackModal(null)
        event.preventDefault();
        setFeedbackModal(<DeleteAccountModal
            title={t('AccountSettingsPage.DeleteModalTitle')}
            body={t('AccountSettingsPage.DeleteModalText')}
            continue={t('AccountSettingsPage.DeleteModalButton')}
            navLink="/login"
            fun={deleteUser}
        ></DeleteAccountModal>)
    }

    let handleChangePassword = (event) => {
        event.preventDefault();
        setFeedbackModal(null)
        setNewPasswordIsLoading(true)
        let response = updatePassword(
            event.target.current_password.value,
            event.target.password.value,
            event.target.password_confirmation.value
        );
        response.
            then((res) => {
                setNewPasswordIsLoading(false);
                setFeedbackModal(
                    <SuccesModal
                        title={t('AccountSettingsPage.PasswordSuccessModalTitle')}
                        body={t('AccountSettingsPage.PasswordSuccessModalText')}
                        continue={t('AccountSettingsPage.PasswordSuccessModalButton')}
                        handleClose={() => { setFeedbackModal(null) }}
                    />
                );
            })
            .catch((error) => {
                setNewPasswordIsLoading(false);
                setFeedbackModal(<ErrorModal
                    title={t('AccountSettingsPage.PasswordFailModalTitle')}
                    body={t('AccountSettingsPage.PasswordFailModalText')}
                    continue={t('AccountSettingsPage.PasswordFailModalButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
                )
            })
    }

    let handleChangeEmailSettings = (event) => {
        event.preventDefault();
        setFeedbackModal(null)
        setUpdateEmailSettingsLoading(true)
        let response = updateEmailSettings(
            event.target.email_updates.checked,
            event.target.email_promotions.checked
        );
        response.
            then((res) => {
                setUpdateEmailSettingsLoading(false);
                setFeedbackModal(
                    <SuccesModal
                        title={t('AccountSettingsPage.EmailSuccessModalTitle')}
                        body={t('AccountSettingsPage.EmailSuccessModalText')}
                        continue={t('AccountSettingsPage.EmailSuccessModalButton')}
                    />
                );
            })
            .catch((error) => {
                setUpdateEmailSettingsLoading(false);
                setFeedbackModal(<ErrorModal
                    title={t('AccountSettingsPage.EmailFailModalTitle')}
                    body={t('AccountSettingsPage.EmailFailModalText')}
                    continue={t('AccountSettingsPage.EmailFailModalButton')}
                />
                )
            })
    }


    let fetchEmailSettings = () => {
        let response = getEmailSettings()
        response.then((res) => {
            setEmailSettings(res.data)
        }).catch((e) => {

        })
    }

    let handleGdprExport = async (format = 'csv') => {
        setFeedbackModal(null)
        setGdprExportLoading(true)

        try {
            const response = await requestGdprExport(format);

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with current date
            const today = new Date().toISOString().split('T')[0];
            link.download = `gdpr_data_export_${today}.zip`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setGdprExportLoading(false);
            setFeedbackModal(
                <SuccesModal
                    title={t('AccountSettingsPage.GdprExportSuccessTitle')}
                    body={t('AccountSettingsPage.GdprExportSuccessText')}
                    continue={t('AccountSettingsPage.GdprExportSuccessButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
            );
        } catch (error) {
            setGdprExportLoading(false);
            setFeedbackModal(
                <ErrorModal
                    title={t('AccountSettingsPage.GdprExportErrorTitle')}
                    body={t('AccountSettingsPage.GdprExportErrorText')}
                    continue={t('AccountSettingsPage.GdprExportErrorButton')}
                    handleClose={() => { setFeedbackModal(null) }}
                />
            );
        }
    }

    useEffect(() => {
        fetchEmailSettings()
        getTeamMembers(getTeamUuid()).then((res) => {
            setMe(res.data["members"].find((member) => member.uuid === getUserUuid()))
        }).catch((e) => {

        })
    }, [])

    return (
        <div className='md:pl-64 pt-4 md:pt-12 w-full flex justify-center px-4'>
            <div className='bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl '>
                <SectionHeader title={t('AccountSettingsPage.PageTitle')} subtitle={t('AccountSettingsPage.PageDescription')} ></SectionHeader>
                <div className="mb-16 space-y-8 divide-y divide-gray-200 sm:space-y-5">
                    <div className="space-y-6 px-8 pt-8 sm:pt-10">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('AccountSettingsPage.AvatarTitle')}</h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('AccountSettingsPage.AvatarDescription')}</p>
                        </div>
                        <div className="flex items-center gap-6 sm:border-t sm:border-gray-200 sm:pt-5">
                            {me
                                ? <MemberAvatar
                                    member={me}
                                    size="h-20 w-20"
                                    textSize="text-xl"
                                    onUploaded={(uuid, avatar_url) => setMe({ ...me, avatar_url })}
                                />
                                : <div className="h-20 w-20 animate-pulse rounded-full bg-gray-100" />
                            }
                            <p className="text-sm text-gray-500">{t('AccountSettingsPage.AvatarHint')}</p>
                        </div>
                    </div>

                    <form className="space-y-8  divide-gray-200 px-8" onSubmit={handleChangePassword}>
                        <div className="space-y-6 pt-8 sm:space-y-5 sm:pt-10">
                            <div>
                                <h3 className="text-lg font-medium leading-6 text-gray-900">{t('AccountSettingsPage.PasswordSettingTitle')}</h3>
                            </div>
                            <div className="space-y-6 sm:space-y-5">
                                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                                    <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                                        {t('AccountSettingsPage.PasswordSettingCurrentPassword')}
                                    </label>
                                    <div className="mt-1 sm:col-span-2 sm:mt-0">
                                        <input
                                            type="password"
                                            name="current_password"
                                            id="current_password"
                                            autoComplete="given-name"
                                            className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:max-w-xs sm:text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                                        {t('AccountSettingsPage.PasswordSettingNewPassword')}
                                    </label>
                                    <div className="mt-1 sm:col-span-2 sm:mt-0">
                                        <input
                                            type="password"
                                            name="password"
                                            id="password"
                                            autoComplete="family-name"
                                            className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:max-w-xs sm:text-sm"
                                        />
                                    </div>
                                </div>

                                <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-gray-200 sm:pt-5">
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                                        {t('AccountSettingsPage.PasswordSettingRepeatPassword')}
                                    </label>
                                    <div className="mt-1 sm:col-span-2 sm:mt-0">
                                        <input
                                            type="password"
                                            name="password_confirmation"
                                            id="password_confirmation"
                                            autoComplete="family-name"
                                            className="block w-full max-w-lg rounded-md border-gray-300 shadow-sm focus:border-black focus:ring-black sm:max-w-xs sm:text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="py-5">
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                >
                                    {t('AccountSettingsPage.PasswordSettingAction')}
                                </button>
                            </div>
                        </div>
                    </form>

                    {/*<form className="space-y-8  divide-gray-200 px-8" onSubmit={handleChangeEmailSettings}>
                        <div className="space-y-6 divide-y divide-gray-200 pt-8 sm:space-y-5 sm:pt-10">
                            <div>
                                <h3 className="text-lg font-medium leading-6 text-gray-900"> {t('AccountSettingsPage.EmailSettingTitle')}</h3>
                                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                    {t('AccountSettingsPage.EmailSettingDescription')}
                                </p>
                            </div>
                            <div className="space-y-6 divide-y divide-gray-200 sm:space-y-5">
                                <div className="pt-6 sm:pt-5">
                                    <div role="group" aria-labelledby="label-email">
                                        <div className="sm:grid sm:grid-cols-3 sm:items-baseline sm:gap-4">
                                            <div>
                                                <div className="text-base font-medium text-gray-900 sm:text-sm sm:text-gray-700" id="label-email">

                                                    {t('AccountSettingsPage.EmailType')}
                                                </div>
                                            </div>
                                            <div className="mt-4 sm:col-span-2 sm:mt-0">
                                                <div className="max-w-lg space-y-4">
                                                    <div className="relative flex items-start">
                                                        <div className="flex h-5 items-center">
                                                            <input
                                                                id="email_updates"
                                                                name="email_updates"
                                                                type="checkbox"
                                                                defaultChecked={emailSettings?.email_updates}
                                                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                                            />
                                                        </div>
                                                        <div className="ml-3 text-sm">
                                                            <label htmlFor="email_updates" className="font-medium text-gray-700">
                                                                {t('AccountSettingsPage.EmailUpdatesTitle')}
                                                            </label>
                                                            <p className="text-gray-500">{t('AccountSettingsPage.EmailUpdatesText')}</p>
                                                        </div>
                                                    </div>
                                                    <div className="relative flex items-start">
                                                        <div className="flex h-5 items-center">
                                                            <input
                                                                id="email_promotions"
                                                                name="email_promotions"
                                                                type="checkbox"
                                                                defaultChecked={emailSettings?.email_promotions}
                                                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                                            />
                                                        </div>
                                                        <div className="ml-3 text-sm">
                                                            <label htmlFor="email_promotions" className="font-medium text-gray-700">
                                                                {t('AccountSettingsPage.EmailPromotionsTitle')}
                                                            </label>
                                                            <p className="text-gray-500">{t('AccountSettingsPage.EmailPromotionsText')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="py-5">
                            <div className="flex justify-end">

                                <button
                                    type="submit"
                                    className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-black py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                >
                                    {t('AccountSettingsPage.EmailPromotionsAction')}
                                </button>
                            </div>
                        </div>
    </form>*/}
                    <div className=" pt-8">
                        <PrivacyDashboard />
                    </div>
                    <div className="space-y-6 divide-gray-200 pt-8 sm:space-y-5 sm:pt-10 px-8">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('AccountSettingsPage.GdprExportTitle')}</h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                {t('AccountSettingsPage.GdprExportDescription')}
                            </p>
                        </div>
                        <div className="space-y-6 sm:space-y-5">
                            <div className="pt-6 sm:pt-5">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={() => handleGdprExport('csv')}
                                        disabled={gdprExportLoading}
                                        type="button"
                                        className="inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {gdprExportLoading ? (
                                            <>
                                                <ButtonParticleLoader className="-ml-1 mr-2" />
                                                {t('AccountSettingsPage.GdprExportLoadingText')}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                {t('AccountSettingsPage.GdprExportButton')}
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    {t('AccountSettingsPage.GdprExportNote')}
                                </p>
                            </div>
                        </div>
                    </div>



                    <div className="space-y-6  divide-gray-200 pt-8 sm:space-y-5 sm:pt-10 px-8">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{t('AccountSettingsPage.DeleteAccountTitle')}</h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                {t('AccountSettingsPage.DeleteAccountText')}
                            </p>
                        </div>
                        <div className="space-y-6  divide-gray-200 sm:space-y-5">
                            <div className="pt-6 sm:pt-5">
                                <div>
                                    <a
                                        href="/login"
                                        onClick={
                                            handleCancelUser
                                        }
                                        type="button"
                                        className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2  sm:w-auto sm:text-sm"
                                    >
                                        {t('AccountSettingsPage.DeleteAccountAction')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
            {feedbackModal}
        </div >
    )
}

export default AccountSettings
